import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Bell, AlertTriangle, Radio, Gauge } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

// Two-tone bell via WebAudio (no external asset). Requires prior user gesture.
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 660].forEach((freq, i) => {
      const start = now + i * 0.18;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.18, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
      o.start(start);
      o.stop(start + 0.4);
    });
    setTimeout(() => ctx.close?.(), 900);
  } catch (err) { console.warn("chime playback failed", err); }
}

function useTitleFlash(enabled) {
  const origRef = useRef(typeof document !== "undefined" ? document.title : "");
  const intervalRef = useRef(null);
  useEffect(() => {
    if (typeof document === "undefined") return;
    origRef.current = origRef.current || document.title;
    const clear = () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      document.title = origRef.current;
    };
    if (!enabled) { clear(); return; }
    const onVisible = () => { if (!document.hidden) clear(); };
    document.addEventListener("visibilitychange", onVisible);
    if (document.hidden && !intervalRef.current) {
      let t = false;
      intervalRef.current = setInterval(() => {
        t = !t;
        document.title = t ? "🚨 Driver drift alert" : origRef.current;
      }, 1200);
    }
    return () => { document.removeEventListener("visibilitychange", onVisible); clear(); };
  }, [enabled]);
}

function alertMeta(a) {
  if (a.type === "speeding") return {
    icon: <Gauge size={14} className="text-rose-600 mt-0.5" />,
    title: `${a.patient_name} · speeding`,
    detail: `${a.speed_mph} mph over ${a.limit_mph} mph limit · ${a.plate || ""}`,
    toast: `${a.patient_name}: ${a.speed_mph} mph (limit ${a.limit_mph})`,
  };
  return {
    icon: <AlertTriangle size={14} className="text-rose-600 mt-0.5" />,
    title: `${a.patient_name} · driver drift`,
    detail: `${a.meters}m off route`,
    toast: `${a.patient_name}: ${a.meters}m off route`,
  };
}

export default function AlertsBell({ onOpenLive }) {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const knownRef = useRef(new Set());
  const firstLoadRef = useRef(true);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useTitleFlash(hasNew);

  const load = () =>
    api.get("/alerts").then((r) => {
      const arr = r.data || [];
      let newOnes = 0;
      arr.forEach((a) => {
        if (!knownRef.current.has(a.id)) {
          knownRef.current.add(a.id);
          if (!firstLoadRef.current) {
            newOnes++;
            const meta = alertMeta(a);
            toast.error(meta.toast, {
              action: { label: "View", onClick: () => onOpenLive?.(a.ride_id) },
            });
          }
        }
      });
      firstLoadRef.current = false;
      if (newOnes > 0) {
        playChime();
        if (document.hidden) setHasNew(true);
      }
      setAlerts(arr);
    }).catch((e) => { console.warn("alerts fetch failed", e); });

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const clearBadge = () => { if (!document.hidden) setHasNew(false); };
    document.addEventListener("visibilitychange", clearBadge);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", clearBadge); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ack = async (id) => {
    try {
      await api.post(`/alerts/${id}/ack`);
      setAlerts((a) => a.filter((x) => x.id !== id));
    } catch (e) { console.warn("ack failed", e); }
  };

  const count = alerts.length;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => { setOpen((o) => !o); setHasNew(false); }}
        className="relative p-2 rounded-md hover:bg-slate-100"
        aria-label="Alerts"
        data-testid="alerts-bell"
      >
        <Bell size={18} className={count ? "text-rose-600" : "text-slate-500"} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] leading-none font-semibold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="alerts-count">
            {count}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 top-11 w-[380px] bg-white border border-slate-200 rounded-md shadow-xl z-50"
          data-testid="alerts-panel"
        >
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="font-display font-semibold text-sm text-slate-900">Live alerts</div>
            <div className="text-xs text-slate-500">{count} active</div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {count === 0 && <div className="empty">No active alerts — all drivers on route and within limits.</div>}
            {alerts.map((a) => {
              const meta = alertMeta(a);
              return (
                <div key={a.id} className="px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`alert-${a.id}`}>
                  <div className="flex items-start gap-2">
                    {meta.icon}
                    <div className="flex-1">
                      <div className="text-sm text-slate-900">
                        <span className="font-medium">{a.patient_name}</span>
                        <span className="text-slate-500"> · {a.type === "speeding" ? "speeding" : "driver drift"}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        <span className="text-rose-700 font-mono">{meta.detail}</span> · {timeAgo(a.at)}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => { setOpen(false); onOpenLive?.(a.ride_id); }}
                          className="text-xs text-white bg-[#0A192F] hover:bg-[#112240] rounded-md px-2.5 py-1 flex items-center gap-1"
                          data-testid={`view-live-${a.id}`}
                        >
                          <Radio size={11} /> View live
                        </button>
                        <button
                          onClick={() => ack(a.id)}
                          className="text-xs text-slate-600 border border-slate-200 hover:bg-slate-50 rounded-md px-2.5 py-1"
                          data-testid={`ack-${a.id}`}
                        >
                          Acknowledge
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
